/**
 * The `item` area: the item page and its peek (src/app/items/, src/app/@modal/).
 * Owned by the stage 2 slot item (docs/I18N.md §11). The keys read in the
 * page's order, top to bottom. The resolver's own words (the act sheet rows,
 * the lifecycle chips, the conclusion question) live in `resolution` and `acts`.
 */
export const item = {
  // --- Route frames (the peek and the section screens, a missing item, the border view) ---
  /** The peek's frame while the item is on its way. */
  'item.peek.loading': 'Loading',
  /** One sentence for a missing and a hidden item alike. */
  'item.route.notFound': 'This item does not exist, or you cannot see it.',
  /** The border view of a private item: what the viewer can and cannot see. */
  'item.border.private':
    'This item is private. You can see that it exists and what it connects to, but not its contents, until you have a role on it.',
  'item.border.privateOwned':
    'This item is private. You can see that it exists, that {owner} is its requester, and what it connects to, but not its contents, until you have a role on it.',
  'item.border.connectsTo': {
    one: 'Connects to · {count} related item',
    other: 'Connects to · {count} related items',
  },
  'item.border.noNeighbours': 'No visible related items at the border.',
  /** How a neighbour on the border ring relates to the item. */
  'item.border.relation.parent': 'parent',
  'item.border.relation.child': 'sub-item',
  'item.border.relation.contributesTo': 'needed by',
  'item.border.relation.dependsOn': 'depends on',
  'item.border.relation.related': 'related',
  /** The trail's root when the item was not opened from a board, a group or a search. */
  // --- Head (the parents line, the title, the meta cluster) ---
  /** The whispered word before the parents line: the item sits in these. */
  'item.parents.in': 'in',
  'item.parents.more': 'More parents',
  'item.title.label': 'Item title',
  'item.title.rename': 'Rename',
  'item.title.copyLink': 'Copy link',
  'item.title.copied': 'Copied',
  // --- State line (the chips under the title, the "+" and the health control) ---
  /** The verdict chip's two words. */
  'item.state.verdict.accepted': 'Accepted',
  'item.state.verdict.denied': 'Denied',
  /** The tooltip on each state chip. */
  'item.state.tip.active': 'Marked as in progress.',
  /** A structural finding on a chip, said in full ahead of the chip's own tip. */
  'item.state.tip.finding.activeAllSettled': 'Still active, though every sub-item is settled.',
  'item.state.tip.finding.doneOpenChildren': 'Done, but sub-items are still open.',
  'item.state.tip.finding.cycle': 'Sits in a cycle of dependencies.',
  'item.state.tip.finding.deadlock': 'Sits in a deadlock: the items wait on each other.',
  'item.state.tip.idle': 'Not marked as in progress.',
  'item.state.tip.review': 'Done, and waiting on its review.',
  'item.state.tip.accepted': 'The review accepted the work.',
  'item.state.tip.denied': 'The review denied the work, so it is open again.',
  'item.state.tip.done': 'Finished.',
  'item.state.tip.cancelled': 'Ended without the work being done.',
  /** The "+" sheet's way into the review composer. */
  /** The "+" that closes the state line, and its Health group. */
  'item.state.add': 'Add a state',
  'item.state.health': 'Health',
  /** The health dot, the worn health chips and the health menu. */
  'item.health.dot': 'Health: {summary}',
  'item.health.nothingDeclared': 'nothing declared',
  'item.health.menu': 'Declared health',
  'item.health.onTrack': 'On track',
  'item.health.clearsAll': 'clears all set states',
  'item.health.concern': 'Concern',
  'item.health.markCritical': '{state}: set to critical',
  /** What a critical declaration costs, stated on the row before the click. */
  'item.health.pageCost': 'Pages parties and subscribers.',
  'item.health.automatic': '{state} (automatic)',
  /** What the mirrored no-requester state means, on its chip in the head. */
  'item.health.unrequestedTip':
    'Nobody wants the result any more. Whoever needs it sets themselves under Requested by.',
  /** The worn state's pill: its words name who set it. */
  'item.health.pill': '{state}, set by {names}',
  /** A flow's rule set it: the flow's name, after the flow sign. */
  'item.health.byFlow': 'per {flow}',
  /** Take one declaration off the state. */
  'item.health.remove': 'Remove',
  /** The suggestion chip: the statements with doors out of them. */
  'item.suggest.count': { one: '{count} suggested', other: '{count} suggested' },
  'item.suggest.label': 'Suggested',
  'item.suggest.tip': 'Statements on this item, and the acts that would answer them.',
  'item.suggest.rule': 'The rule ({flow})',
  /** Who may take a door the viewer may not. */
  'item.suggest.notYours': 'not yours to take',
  'item.suggest.inRoleRequester': 'Requester · {names}',
  'item.suggest.inRoleEditor': 'Editor · {names}',
  // --- Statements (each rendered on what it concerns) ---
  /** The acts on a statement: set, not affected, note. */
  'item.statement.adopt': 'Set to {state}',
  'item.statement.adoptCost': 'a health state in your name, citing this',
  'item.statement.contain': 'Not affected',
  'item.statement.containCost': "won't reach items depending on this one",
  'item.statement.addNote': 'Add a note…',
  'item.statement.noteLabel': 'Note on this statement',
  'item.statement.noteSubmit': 'Add note',
  'item.statement.noteCancel': 'Cancel',
  /** A statement as values: the field it is about, before its value. */
  'item.statement.value.end': 'End',
  'item.statement.value.start': 'Start',
  'item.statement.value.noActivity': 'No activity',
  'item.statement.value.goalUntended': 'Goal untended',
  'item.statement.value.dependents': 'Dependents',
  'item.statement.value.readyDependents': 'Ready dependents waiting',
  'item.statement.value.noRequester': 'No requester',
  'item.statement.value.noContributor': 'No contributor',
  'item.statement.value.drift.doneOpenChildren': 'Done, open below',
  'item.statement.value.drift.activeAllSettled': 'Active, all settled below',
  'item.statement.value.deadlock': 'Dependency deadlock',
  'item.statement.value.cycle': 'Dependency cycle',
  /** After a cycle's items: it also runs through items the viewer can
   *  neither open nor holds as stubs (never how many). */
  'item.statement.value.unknownParticipants': 'with unknown participants',
  /** After a relayed statement: its path runs through such items. */
  'item.statement.value.viaUnknown': 'via unknown items',
  'item.statement.value.statement': 'A statement',
  /** What a stage control's finding sign says to a screen reader, after the chip's own word. */
  'item.statement.signWords': ', {statement}',
  // --- Needs you (the attention strip, the review, the conclusion question) ---
  /** The answers to a seat offer (its sentence is `feed.offer.*`), and the item in Review that awaits the viewer's verdict. */
  'item.attention.takeUp': 'Accept',
  'item.attention.decline': 'Decline',
  /** The item in Review that awaits the viewer: who handed it in (the one
   *  contributor), or the plain fact when there is no single contributor; then
   *  the ask, as a sentence of its own (docs/CLOSED_AXIS.md "Finishing"). */
  'item.attention.deliveredBy': '{name} marked this ready for review.',
  'item.attention.delivered': 'The work is ready for review.',
  'item.attention.reviewAsk': 'It waits for your review: accept it or deny it.',
  /** The needs-you strip's ✕: hides the strip for the viewer until the next
   *  ask. The glossary's "Hide": nothing on the work changes. */
  'item.attention.dismiss': 'Hide',
  /** The conclusion question's composer: each open sub-item, and the commit,
   *  in the word of the gesture that asked (Done finishes, Cancel cancels). */
  'item.conclusion.finishWith': 'Finish with it: {title}',
  'item.conclusion.cancelWith': 'Cancel with it: {title}',
  'item.conclusion.listed': 'another list holds it',
  'item.conclusion.needed': 'other work needs it',
  'item.conclusion.finishOnly': 'Finish this one only',
  'item.conclusion.cancelOnly': 'Cancel this one only',
  'item.conclusion.finishMore': {
    one: 'Finish this and {count} more',
    other: 'Finish this and {count} more',
  },
  'item.conclusion.cancelMore': {
    one: 'Cancel this and {count} more',
    other: 'Cancel this and {count} more',
  },
  // --- Meta group (the board context, the views on the item, the flow distribution, the dates) ---
  /** A parent's flow distribution over its member descendants: "below: Build: 2 in Doing, 1 in Review". */
  'item.meta.flowDistribution': 'below: {flows}',
  'item.meta.flowCounts': '{flow}: {counts}',
  'item.meta.flowCount': '{count} in {stage}',
  'item.meta.flowDistributionTip':
    "Derived from this item's descendants that are flow members. Inference only: shown beside the item's own declared state, never written back - flow membership never cascades through decomposition.",
  /** The Details grid (the item's properties): its section label and its row labels. */
  'item.properties.title': 'Details',
  'item.properties.requestedBy': 'Requested by',
  /** What the requester seat's label means, in its tooltip. */
  'item.properties.requestedByTip': 'Needs the result and asked for this item',
  /** The Review required setting (docs/CLOSED_AXIS.md, 2026-11-03). */
  'item.properties.review': 'Review',
  'item.properties.reviewTip':
    'Whether the work goes through review before it is done. The requester sets it.',
  'item.properties.reviewRequired': 'Required',
  'item.properties.reviewOptional': 'Not required',
  'item.properties.reviewByFlow': 'The flow requires a review all the same.',
  'item.properties.tags': 'Tags',
  'item.properties.created': 'Created',
  'item.properties.updated': 'Updated',
  /** The board the item was opened from, and how that board groups it. */
  'item.viewContext.via': 'Via the board {board}',
  'item.viewContext.lane': 'Column',
  'item.viewContext.unset': 'Unset',
  'item.viewContext.unassigned': 'Unassigned',
  /** The views the item is on (the Views row and its picker, docs/VIEWS.md §2b
   *  "An item's views"): the row's label, which is also the picker's title. */
  'item.views.label': 'Boards',
  /** The row's one act, opening the picker. */
  'item.views.add': 'Add to board',
  /** The row's act while the item is on no board: the dashed add chip's
   *  word, after its plus. */
  'item.views.board': 'Board',
  /** A view's relation to the item, as the row's link title. */
  'item.views.titleRootChecklist': 'A board of its sub-items, as a checklist',
  'item.views.titleRootList': 'A board of its sub-items, as a list',
  'item.views.titleRootBoard': 'A board of its sub-items, as a kanban',
  'item.views.titlePinBoard': 'Pinned on this board',
  'item.views.titleParent': 'On this board through its parent',
  'item.views.titleAncestor': 'On this board through an item above it',
  /** The same relation, quieter, beside a view's name in the picker. */
  'item.views.relRoot': 'its sub-items',
  'item.views.relParent': 'through its parent',
  'item.views.relAncestor': 'through an item above it',
  /** The act that takes the item off a view, and its accessible name. */
  'item.views.unpin': 'Unpin',
  'item.views.unpinFrom': 'Unpin from {view}',
  'item.views.hide': 'Hide',
  'item.views.hideOn': 'Hide on {view}',
  /** The picker's search over the boards to pin the item to. */
  'item.views.search': 'Pin to a board…',
  'item.views.searchLabel': 'Pin to a board',
  'item.views.boards': 'Boards',
  'item.views.noMatch': 'No board matches.',
  'item.views.noBoards': 'No other board to pin it to.',
  /** Why a board is muted in the search. */
  'item.views.reasonViewRole': 'you can see this board, not curate it',
  'item.views.reasonShare': 'shared board - pinning needs owner authority on the item',
  /** The two views the item can make of its sub-items. */
  'item.views.newChecklist': 'New board of its sub-items, as a checklist',
  'item.views.newBoard': 'New board of its sub-items, as a kanban',
  'item.views.newLabel': 'New board',
  // --- Seats (the requester and contributor fields, the promise) ---
  /** The two seat fields: the empty seat's name, the field's title, the picker's name. */
  'item.seats.contributor.empty': "Contributor seat open - take it on or set who's doing it",
  'item.seats.contributor.title': "Take this on, or set who's doing it",
  'item.seats.contributor.menu': 'Contributors',
  'item.seats.contributor.word': 'Contributor',
  /** The held contributor seat's tooltip and accessible name: the role, then who. */
  'item.seats.contributor.holder': 'Contributor: {names}',
  /** The empty requester seat's value in the Details grid. */
  'item.seats.requester.nobody': 'nobody',
  'item.seats.requester.empty': 'Requester seat open - set the requester',
  'item.seats.requester.title': 'Set the requester(s)',
  'item.seats.requester.menu': 'Requesters',
  'item.seats.remove': 'Remove {name}',
  'item.seats.offered': 'offered',
  'item.seats.offeredTo': 'Offered to {name}.',
  'item.seats.takeUp': 'Take up',
  'item.seats.takeUpForGroup': 'Take up for the group',
  'item.seats.declineOffer': 'Decline this offer',
  'item.seats.withdrawOffer': 'Withdraw the offer to {name}',
  'item.seats.takeThisOn': 'Take this on',
  'item.seats.takeThisOnHint': 'add yourself as contributor',
  'item.seats.search': 'Search people…',
  'item.seats.noOne': 'No one to add.',
  'item.seats.needsDoing': 'Needs doing',
  /** Making a dependency a promise: a contract the requester owns, its contributor seat offered. */
  'item.promise.title': 'Make this a promise',
  'item.promise.subtitle':
    "A contract owned by this item's requester; the contributor seat is offered - by their rules, never forced.",
  'item.promise.prefill': 'Finish {title}',
  'item.promise.field': 'The promise',
  'item.promise.offeredTo': 'Offered to',
  'item.promise.offeredToHint':
    "Leave empty to offer it to the item's current requester(s). Their push rules decide: settled directly, or offered for take-up.",
  'item.promise.chooseOther': 'Choose someone else',
  'item.promise.search': 'Search people and groups…',
  'item.promise.noOne': 'No one to offer it to.',
  'item.promise.neededBy': 'Needed by',
  'item.promise.neededByHint': 'Optional - any precision: 2026-09-30, 2026-W40, 2026-Q4.',
  'item.promise.cancel': 'Cancel',
  'item.promise.confirm': 'Make it a promise',
  // --- Access (the access trigger and its panel) ---
  'item.access.title': 'Access',
  'item.access.nobody': 'nobody reaches this yet',
  /** The two access states that earn a word: the item is open, or it does not inherit access. */
  'item.access.openToEveryone': 'open to everyone',
  'item.access.chainBroken': 'access chain broken',
  /** The seat a person with access also holds, in the access tooltip. */
  'item.access.hat.contributor': 'contributor',
  'item.access.hat.requester': 'requester',
  'item.access.hat.both': 'requester · contributor',
  /** The roles a person or group can be added as, with what each does. */
  'item.access.role.contributor': 'Contributor',
  'item.access.role.contributorHint': 'does the work',
  'item.access.role.requester': 'Requester',
  'item.access.role.requesterHint': 'owns the outcome',
  'item.access.role.maintainer': 'Maintainer',
  'item.access.role.maintainerAddHint': 'manages it',
  'item.access.role.maintainerHint': 'manages',
  'item.access.role.member': 'Member',
  'item.access.role.memberAddHint': 'can work it',
  'item.access.role.memberHint': 'can work',
  'item.access.role.viewer': 'Viewer',
  'item.access.role.viewerHint': 'can see',
  /** A grant's role inside a sentence. */
  'item.access.roleWord.viewer': 'viewer',
  'item.access.roleWord.member': 'member',
  'item.access.roleWord.maintainer': 'maintainer',
  /** The paths by which a person reaches the item, as chips beside their role. */
  'item.access.path.requester': 'requester',
  'item.access.path.requesterTitle': 'The requester: owns the outcome',
  'item.access.path.coRequester': 'A co-requester of this item',
  'item.access.path.contributor': 'contributor',
  'item.access.path.contributorTitle': 'The contributor: does the work',
  'item.access.path.coContributor': 'A co-contributor on this item',
  'item.access.path.direct': 'direct',
  'item.access.path.directTitle': 'Granted directly on this item: {role}',
  'item.access.path.inherited': 'inherited',
  'item.access.path.inheritedFrom': '{role} inherited from "{source}"',
  /** An inherited path whose source the viewer sees only as its stub: no role
   *  its people hold there. */
  'item.access.path.inheritedFromStub': 'Inherited from "{source}"',
  'item.access.path.inheritedFromUnknown': 'Inherited from an item you cannot see',
  /** The label before the hidden item value of an unknown grant source. */
  'item.access.unknownSource': 'Inherited from',
  'item.access.roleFor': 'Role for {name}',
  'item.access.directGrant': 'Direct grant: {role}',
  'item.access.remove': 'Remove {name}',
  'item.access.addPlaceholder': 'Add people and groups…',
  'item.access.footnote': 'Access changes are recorded in the activity log.',
  /** Inviting someone by email, and the invites still pending. */
  'item.access.invite.label': 'Invite by email',
  'item.access.invite.placeholder': 'name@example.com',
  'item.access.invite.email': 'Email address to invite',
  'item.access.invite.role': 'Role for the invited person',
  'item.access.invite.send': 'Invite',
  'item.access.invite.tag': '{status} · {role}',
  'item.access.invite.invited': 'invited',
  'item.access.invite.expired': 'expired',
  'item.access.invite.revoke': 'Revoke invite for {email}',
  // --- The phone's doors (phone-doors.tsx, docs/MOBILE.md §3, 2026-11-02) ---
  /** The Relationships door's digest: the open sub-items over every depth,
   *  the rows of the other groups, or both. */
  'item.doors.dependencies': { one: '{count} dependency', other: '{count} dependencies' },
  /** The Activity door's digest: what came in since the viewer last read,
   *  then when the item last moved. */
  'item.doors.unread': { one: '{count} new', other: '{count} new' },
  'item.doors.activity': '{unread} · {time}',
  // --- Files (attachments-section.tsx, docs/ATTACHMENTS.md §5) ---
  'item.files.title': 'Files',
  /** The Files screen's add line: opens the file picker. */
  'item.files.add': 'Add a file',
  /** A file row's menu: its accessible name, then its one act. */
  'item.files.rowMenu': 'File "{name}"',
  'item.files.remove': 'Remove',
  /** A pending row while its bytes are on their way. */
  'item.files.uploading': 'Uploading',
  /** An upload that got no answer (the connection dropped, a proxy refused it). */
  'item.files.uploadFailed': 'The file was not uploaded.',
  /** Hides a refused upload's row. */
  'item.files.dismiss': 'Hide',
  // --- Sections (the added sections: flows, resources, schedule, cadence and fields) ---
  /** The added sections: the add row, each section's rest line and editor. */
  'item.sections.add': 'Add a section',
  /** The add menu's rows for the two built-in sections an item may lack: the
   *  description (while it has none) and its files (the row opens the picker). */
  'item.sections.description': 'Description',
  'item.sections.files': 'Files…',
  'item.sections.nothingToAdd': 'Nothing to add.',
  'item.sections.notSet': 'not set',
  'item.sections.noPosition': 'no position',
  'item.sections.restLine': '{label}: {value}',
  'item.sections.done': 'OK',
  'item.sections.remove': 'Remove {section}',
  'item.sections.removeTitle': 'Remove {section} from this item.',
  'item.sections.removeConfirm': 'Remove {section}? Its values are cleared.',
  'item.sections.confirmRemove': 'confirm remove',
  'item.sections.confirmRemoveLabel': 'Confirm removing {section}',
  /** A flow's own section: where the item stands in it, and leaving it. */
  'item.sections.flowFieldSet': 'A field set, no stages.',
  'item.sections.flowPosition': 'In {stage} · stage {index} of {count}.',
  'item.sections.flowPositionGone': 'Position "{key}" is no longer among this flow\'s stages.',
  'item.sections.leave': 'leave flow',
  'item.sections.leaveTitle':
    'Remove this item from the flow. The position it abandons stays in the log.',
  'item.sections.leaveConfirm': 'Leave this flow? The abandoned position stays in the log.',
  'item.sections.confirmLeave': 'confirm leave',
  /** The Resources section. */
  'item.sections.allocation': 'Allocation',
  'item.sections.overCommitted': 'over 1.0 FTE, over-committed',
  'item.sections.estimate': 'Estimate',
  'item.sections.budget': 'Budget',
  /** The units and cadences a value is counted in. */
  'item.sections.unit.fte': 'FTE',
  'item.sections.unit.percent': '%',
  'item.sections.unit.days': 'days',
  'item.sections.unit.hours': 'hours',
  'item.sections.unit.weeks': 'weeks',
  'item.sections.unit.months': 'months',
  /** An amount counted in a unit: the unit's word follows the count (a
   *  fraction takes the plural). FTE and % count nothing, so they have none. */
  'item.sections.unitCount.days': { one: '{count} day', other: '{count} days' },
  'item.sections.unitCount.hours': { one: '{count} hour', other: '{count} hours' },
  'item.sections.unitCount.weeks': { one: '{count} week', other: '{count} weeks' },
  'item.sections.unitCount.months': { one: '{count} month', other: '{count} months' },
  'item.sections.cadence.oneTime': 'one-time',
  'item.sections.cadence.perWeek': '/ week',
  'item.sections.cadence.perMonth': '/ month',
  'item.sections.cadence.perYear': '/ year',
  /** The Schedule section: its two edges, duration and cadence, and its rest line. */
  'item.schedule.startLabel': 'Start',
  'item.schedule.endLabel': 'End',
  'item.schedule.durationLabel': 'Duration',
  'item.schedule.cadenceLabel': 'Cadence',
  'item.schedule.everyWord': 'every',
  'item.schedule.mode': '{label} mode',
  'item.schedule.modeDate': 'Date',
  'item.schedule.modeAfter': 'After item',
  'item.schedule.anchor': 'Anchor item',
  'item.schedule.chooseItem': 'choose item…',
  'item.schedule.resolves': 'resolves once {item} has a date',
  'item.schedule.thatItem': 'that item',
  'item.schedule.start': 'Start {date}',
  'item.schedule.end': 'End {date}',
  'item.schedule.after': 'after {item}',
  'item.schedule.afterOffset': 'after {item} + {amount}',
  'item.schedule.anItem': 'item',
  /** A stored amount the plural rule cannot count (a unit no table names, or
   *  a value that is not a number), printed as stored. */
  'item.schedule.amountAsStored': '{value} {unit}',
  /** The cadence on the rest line, counted. */
  'item.schedule.every.days': { one: 'every {count} day', other: 'every {count} days' },
  'item.schedule.every.hours': { one: 'every {count} hour', other: 'every {count} hours' },
  'item.schedule.every.weeks': { one: 'every {count} week', other: 'every {count} weeks' },
  'item.schedule.every.months': { one: 'every {count} month', other: 'every {count} months' },
  'item.schedule.everyAsStored': 'every {value} {unit}',
  /** What moving the end date costs, stated before the move. */
  'item.schedule.conflictDependents': {
    one: "conflicts with {count} dependent's schedule",
    other: "conflicts with {count} dependents' schedules",
  },
  'item.schedule.conflictDependencies': {
    one: "a dependency's declared end lands after this date",
    other: "{count} dependencies' declared ends land after this date",
  },
  /** A flow's own fields. */
  'item.sections.editField': 'Edit {field}',
  'item.sections.addField': 'Add {field}…',
  'item.sections.defined': { one: '{count} defined', other: '{count} defined' },
  'item.sections.noneDefined': 'none defined',
  // --- Flow (the flow strip, joining a flow, queueing for review) ---
  /** The act sheet's Advance refusal when the boundary names no unmet condition. */
  'item.flow.gateClosed': 'This item may not cross into "{stage}" yet.',
  /** The flow strip: one flow's stages, the item's position and the moves from it. */
  'item.flow.strip': 'Flow: {flow}',
  'item.flow.somethingWrong': 'Something went wrong.',
  'item.flow.unreached': 'The action failed to reach the server. Reload the page and try again.',
  'item.flow.stageTipMeans': '{stage}: {state} · means {means}',
  'item.flow.stageTipManual': '{stage}: {state} · a manual word',
  'item.flow.current': 'current',
  /** The item page's note after a gesture became an ask: who, and what. */
  'item.asked': '{asked}: {statement}',
  /** A stage's state for this member (src/lib/flow-vocab.ts). */
  'item.flow.state.passed': 'passed',
  'item.flow.state.current': 'current',
  'item.flow.state.open': 'open',
  'item.flow.state.blocked': 'blocked',
  /** What a boundary means, by its mode (src/lib/flow-vocab.ts). */
  'item.flow.gateHint.enforced':
    'Decision required: advancing past this boundary is a recorded decision its decider takes (owners may act through, but only loudly, at critical level).',
  'item.flow.gateHint.open':
    'Open boundary: the move always succeeds; unmet expectations flag it as a fact, never block it.',
  'item.flow.deciderTip': 'Decider: {name} takes the advance decision at this boundary.',
  'item.flow.decisionRequired': 'decision required',
  'item.flow.decisionRequiredBy': 'decision required: {name}',
  'item.flow.decider': 'decider',
  'item.flow.deciderNamed': 'decider: {name}',
  'item.flow.restricted': 'restricted item',
  /** Where a condition item of a stage stands. */
  'item.flow.condition.accepted': 'accepted',
  'item.flow.condition.disputed': 'denied',
  'item.flow.condition.notVisible': 'not visible',
  'item.flow.condition.delivered': 'done',
  'item.flow.condition.notDelivered': 'not done',
  'item.flow.condition.waived': 'waived',
  'item.flow.waive': 'waive',
  'item.flow.waiveTitle':
    "Waive this condition for this member - a logged escape, visible forever. Needs this stage's decider or owner reach.",
  'item.flow.advance': 'Advance here',
  'item.flow.advanceTitle': 'Advance the member across this boundary (its conditions decide).',
  'item.flow.moveBack': 'Move back here',
  'item.flow.moveBackTitle': 'Move the member back here. Regression is a free, first-class move.',
  'item.flow.applies': 'applies {means}',
  'item.flow.hold': 'Hold',
  'item.flow.holdTitle':
    "Record a no-go at this boundary: reviewed, decided not to advance (yet). Needs this stage's decider or owner reach.",
  'item.flow.holdReason': 'Reason (optional)…',
  'item.flow.holdReasonLabel': 'Reason for holding',
  'item.flow.addWork': 'Add work for {stage}',
  'item.flow.addWorkPlaceholder': 'Add work for {stage}…',
  'item.flow.addWorkLabel': 'Work for stage {stage}',
  'item.flow.addWorkTitle': 'Create an item that must be accepted before this boundary clears.',
  'item.flow.add': 'Add',
  /** Joining a flow: pick the flow, then the stage it honestly stands at. */
  'item.join.title': 'Join a flow',
  'item.join.open': 'Join a flow…',
  'item.join.search': 'Search flows…',
  'item.join.searchLabel': 'Search flows to join',
  'item.join.noMatch': 'No matching flow.',
  'item.join.shareBlocked': 'needs an item owner to share',
  'item.join.shareBlockedTitle':
    'Joining shares this item with the flow’s audience; that needs an owner of the item.',
  'item.join.into': 'Join {flow} {id}',
  'item.join.hint':
    'Where does it honestly stand? Joining declares position only - no conditions are checked.',
  'item.join.atStage': 'Join at stage',
  'item.join.back': 'Back',
  'item.join.confirm': 'Join',
  /** Putting the next stage decision on a ceremony's agenda instead of taking it now. */
  'item.queue.label': 'Queue for review',
  'item.queue.ceremony': 'Ceremony to queue into',
  'item.queue.advance': 'Advance to {stage}',
  'item.queue.hold': 'Hold',
  'item.queue.unreached': 'The action failed to reach the server. Reload and try again.',
  // --- Proposed changes (the proposed changes section and the propose form) ---
  'item.proposed.title': 'Proposed changes',
  'item.proposed.accept': 'Accept',
  'item.proposed.decline': 'Decline',
  'item.proposed.empty': 'Nothing proposed on this item.',
  /** Where a proposed change stands. */
  'item.proposed.status.pending': 'open',
  'item.proposed.status.applied': 'applied',
  'item.proposed.status.declined': 'declined',
  'item.proposed.status.superseded': 'superseded',
  'item.proposed.status.expired': 'expired',
  /** The composer that proposes a change, now or into a plan. */
  'item.proposed.open': 'Propose a change',
  'item.proposed.fieldPick': 'Field to propose',
  'item.proposed.field.priority': 'Priority',
  'item.proposed.field.acceptanceCriteria': 'Acceptance criteria',
  'item.proposed.value': 'Proposed value',
  'item.proposed.newValue': 'New value',
  'item.proposed.clear': '(clear)',
  'item.proposed.priority.must': 'must',
  'item.proposed.priority.should': 'should',
  'item.proposed.priority.could': 'could',
  'item.proposed.target': 'Where to put it',
  'item.proposed.now': 'Propose now',
  'item.proposed.addTo': 'Add to: {plan}',
  'item.proposed.addToNew': 'Add to a new plan…',
  'item.proposed.newPlanName': 'New plan name',
  'item.proposed.submit': 'Propose',
  'item.proposed.add': 'Add',
  'item.proposed.cancel': 'Cancel',
  // --- Rail (the lineage minimap and the flow position) ---
  'item.rail.flowPosition': 'Flow position',
  'item.lineage.title': 'Lineage',
  'item.lineage.settings': 'Lineage settings',
  'item.lineage.settingsChanged': 'Lineage settings (changed)',
  'item.lineage.depth': 'Depth',
  'item.lineage.depthDown': 'Less depth',
  'item.lineage.depthUp': 'More depth',
  'item.lineage.related': 'Related items',
  'item.lineage.hideRelated': 'Hide related items',
  'item.lineage.showRelated': 'Show related items',
  'item.lineage.closed': 'Closed',
  'item.lineage.hideClosed': 'Hide closed',
  'item.lineage.showRecentlyClosed': 'Show recently closed',
  'item.lineage.showClosed': 'Show closed',
  'item.lineage.graph': 'Lineage graph',
  'item.lineage.capped': 'Related work capped; open the map for the full graph.',
  // --- Sub-items (the sub-items section) ---
  'item.subItems.title': 'Sub-items',
  /** The way from the item's list to its own checklist board. */
  'item.list.open': 'Open as checklist',
  'item.subItems.digest': '{closed} of {total} closed',
  'item.subItems.add': 'Add a sub-item',
  'item.subItems.showClosed': { one: 'Show {count} closed', other: 'Show {count} closed' },
  'item.subItems.hideClosed': { one: 'Hide {count} closed', other: 'Hide {count} closed' },
  // --- Relationships (the relationships section and its editor) ---
  'item.relationships.title': 'Relationships',
  /** What the Relationships door row says after its count, to a screen reader: how many related items or statements stand at the worst health. */
  'item.relationships.doorCritical': { one: ', one critical', other: ', {count} critical' },
  'item.relationships.doorWarning': { one: ', one warning', other: ', {count} warning' },
  /** Links another item asks for, waiting on this item's answer. */
  'item.relationships.requests': 'Requests · {count}',
  'item.relationships.wantsParent': 'wants this as parent',
  'item.relationships.wantsDependency': 'wants to depend on this',
  'item.relationships.wantsRelated': 'wants this as related',
  'item.relationships.requested': 'requested',
  'item.relationships.withdraw': 'withdraw',
  'item.relationships.approve': 'approve',
  'item.relationships.decline': 'decline',
  /** The groups of links, each by the relationship's word. */
  'item.relationships.partOf': 'Part of',
  'item.relationships.dependsOn': 'Depends on',
  'item.relationships.neededBy': 'Needed by',
  /** On the Needed by group's head: how many of the items that need this one are ready and wait on it alone. */
  'item.relationships.readyWaiting': {
    one: '1 ready, waiting on this',
    other: '{count} ready, waiting on this',
  },
  'item.relationships.subItems': 'Sub-items',
  'item.relationships.related': 'Related',
  /** A row's sheet: what arrived through the row and the link's own acts. */
  'item.relationships.rowSheet': 'Relationship with "{title}"',
  'item.relationships.reshape': 'Change relationship…',
  'item.relationships.promise': 'Make this a promise…',
  'item.relationships.uncontain': 'Remove "Not affected"',
  'item.relationships.stub.notShared': 'Not shared with you.',
  'item.relationships.stub.requester': 'Requester',
  'item.relationships.stub.requestAccess': 'Request access',
  'item.relationships.orphanContained': 'Not affected, no longer linked',
  /** The composer that links or creates an item with a relationship. */
  'item.relationships.addGhost': 'Add a relationship…',
  'item.relationships.addField': 'Add a relationship',
  'item.relationships.type': 'Relationship type',
  'item.relationships.add.sub': 'sub-item',
  'item.relationships.add.subPlaceholder': 'Add a sub-item: type a name and press Enter…',
  'item.relationships.add.parentPlaceholder': 'Part of: type a name and press Enter…',
  'item.relationships.add.blockerPlaceholder': 'Depends on: type a name and press Enter…',
  'item.relationships.add.feedsPlaceholder': 'Needed by: type a name and press Enter…',
  'item.relationships.add.relatedPlaceholder': 'Related: type a name and press Enter…',
  'item.relationships.addButton': 'add',
  'item.relationships.orLink': 'or link an existing item',
  'item.relationships.linkAs': 'link as {kind}',
  'item.relationships.somethingWrong': 'Something went wrong.',
  'item.relationships.notSaved': 'Not saved. Retry',
  /** The dialog that changes what a link is, or removes it. */
  'item.relationships.dialogTitle': 'Relationship',
  'item.relationships.with': 'With {title}',
  'item.relationships.current': ' · current',
  'item.relationships.shape.sub': 'Sub-item',
  'item.relationships.shape.subHint': 'contained below; rolls up into this item',
  'item.relationships.shape.parent': 'Part of',
  'item.relationships.shape.parentHint': 'this item sits inside it',
  'item.relationships.shape.blocker': 'Depends on',
  'item.relationships.shape.blockerHint': 'this item needs it done',
  'item.relationships.shape.feeds': 'Needed by',
  'item.relationships.shape.feedsHint': 'it needs this item done',
  'item.relationships.shape.related': 'Related',
  'item.relationships.shape.relatedHint': 'a soft link, no dependency',
  'item.relationships.sentForApproval':
    'Sent for approval - the other side must accept the new link.',
  'item.relationships.removeLink': 'Remove link',
  'item.relationships.cancel': 'Cancel',
  'item.relationships.apply': 'Apply',
  /** The sub-items cascade: folding a row, and dragging it into order. */
  'item.relationships.move': 'Move "{title}"',
  'item.relationships.moveUnreached': 'The move did not reach the server. Try again.',
  // --- Activity (the item's history) ---
  'item.activity.title': 'Activity',
  // --- Dock (the dock and the item menu) ---
  'item.dock.back': 'Back',
  /** The section screen's commit while its editor is open (the description). */
  'item.dock.done': 'Done',
  /** The dock's "…" pod and the menu it opens. */
  'item.dock.about': 'About this item',
  'item.dock.thisItem': 'This item',
  'item.dock.rename': 'Rename',
  'item.dock.access': 'Access',
  /** The accent act while work marked done awaits the viewer's verdict under an
   *  authored review duty. */
  'item.dock.nextStep': 'Next step',
  /** The desktop kebab and the rows it shares with the dock's "…". */
  'item.menu.label': 'Item actions',
  'item.menu.more': 'More actions',
  'item.menu.propose': 'Propose a change…',
  'item.menu.openInNewTab': 'Open in new tab',
  'item.menu.addNote': 'Add a note',
  'item.menu.editNote': 'Edit the note',
  /** The notify choice: one row, and the three values it opens. */
  'item.notify.row': 'Notify me…',
  'item.notify.label': 'Notify me',
  'item.notify.subscribe': 'Subscribe',
  'item.notify.default': 'Default',
  'item.notify.hide': 'Hide',
  /** The act sheet: its name, and the words its rows wear beside the verb. */
  'item.acts.sheet': 'Acts on this item',
  'item.acts.outOfRole': 'out of role',
  'item.acts.requesterOnly': 'requester only',
  'item.acts.requesterOnlyTitle': 'Only the requester seat can cancel this',
  'item.acts.viewOnly': 'view only',
  /** How many ancestors a close takes with it. */
  'item.acts.closesAbove': '+{count} above',
  'item.acts.closeAll': 'Finish all?',
  /** The item page's server actions refuse (src/app/actions.ts). */
  'item.error.unknownGrade': 'Unknown severity grade.',
  'item.error.renameNeedsQuestion': 'This rename needs a question the title cannot ask.',
  'item.error.inviteMailFailed':
    'The invite was recorded but the email could not be sent. Try re-inviting.',
} as const;
